import { ROUTE_COMMON, ROUTES_SUPERVISORS } from '@/app/data/route_constants';
import router from '@/router';
import { ProfileRecord, PROFILE_TYPES } from '@/app/data/model_constants';
import { useGlobalConfirm } from '@/composables/useGlobalConfirm';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

export default function useProfileImpersonation() {
  const store = useStore();
  const { confirm } = useGlobalConfirm();
  const { t } = useI18n();

  const canImpersonateProfile = (currentProfile, targetProfile) => {
    if (!targetProfile) return false;

    // Supervisors can impersonate anyone
    if (currentProfile.type === PROFILE_TYPES.SUPERVISOR) return true;

    // Non-supervisors can't impersonate employees
    if (targetProfile.type === PROFILE_TYPES.EMPLOYEE) return false;

    return true;
  };

  const isProfileActive = async (profile) => {
    const isInactiveProfile = ![
      ProfileRecord.STATUS_INVITED,
      ProfileRecord.STATUS_ACTIVE
    ].includes(profile.attributes.status);

    if (!isInactiveProfile) return true;

    await confirm({
      message: t('contact.labels.impersonateInactiveProfile'),
      header: 'Oh no!',
      accept_label: 'Got it'
    });
    return false;
  };

  const getSubdomain = (profile, project) => ((project?.attributes.dedicated)
    ? project.attributes.subdomain
    : profile.attributes.subdomain);

  const generateProjectUrl = (origin, profile, project) => `${origin}/customer/${profile.attributes.client_id}/project/${project.id}/dashboard`
      + `?_profile_type=${profile.type}&_profile_id=${profile.id}`;

  const generateNonProjectUrl = (origin, profile, routePath) => [
    'https://',
    origin,
    `${routePath}${profile.type.toLowerCase()}/${profile.attributes.client_id}`,
    `?_profile_type=${profile.type}&_profile_id=${profile.id}`
  ].join('');

  const generateImpersonationUrl = async (profile, project) => {
    if (!profile) return null;

    const current_profile = store.getters.current_profile;
    if (!canImpersonateProfile(current_profile, profile)) return null;

    if (!await isProfileActive(profile)) return null;

    const subdomain = getSubdomain(profile, project);
    const baseOrigin = window.location.origin;

    // Handle project-specific URL if project is provided
    if (project?.attributes.dedicated && profile.type === ProfileRecord.TYPE_CUSTOMER) {
      const origin = baseOrigin.replace(/^https:\/\/[^.]*\./, `https://${subdomain}.`);
      return generateProjectUrl(origin, profile, project);
    }

    // Original URL generation for non-project cases
    const origin_tenant = baseOrigin.replace(/^[^.]*/, subdomain);
    const route_path = router.resolve({ name: ROUTE_COMMON.ROOT }).href;
    return generateNonProjectUrl(origin_tenant, profile, route_path);
  };

  const setImpersonationState = (originalUrl, impersonationUrl) => {
    localStorage.setItem('impersonationOriginalUrl', originalUrl);
    localStorage.setItem('impersonationUrl', impersonationUrl);
  };

  const clearImpersonationState = () => {
    localStorage.removeItem('impersonationOriginalUrl');
    localStorage.removeItem('impersonationUrl');
  };

  const impersonate = async (profile, { project = null, only_url = false, open_in_new_tab = false } = {}) => {
    try {
      const url = await generateImpersonationUrl(profile, project);
      if (!url) return undefined;

      clearImpersonationState();
      setImpersonationState(window.location.href, url);

      if (only_url) return url;
      if (open_in_new_tab) {
        const newWindow = window.open(url, '_blank');
        if (newWindow === null) {
          throw new Error('Unable to open new window. Please check if popup blocker is enabled.');
        }
      } else {
        window.location.href = url;
      }
    } catch (error) {
      return undefined;
    }
    return undefined;
  };

  const stopImpersonation = () => {
    localStorage.removeItem('_profile_id');
    localStorage.removeItem('_profile_type');

    const current_profile = store.getters.current_profile
    const current_profile_true = store.getters.current_profile_true

    if (current_profile_true.type === PROFILE_TYPES.SUPERVISOR) {
      const { subdomain } = current_profile_true.attributes;
      const origin_tenant = `${window.location.origin.replace(/^https:\/\/[^.]*\./, `https://${subdomain}.`)}`;
      const route_path = router.resolve({
        name: ROUTES_SUPERVISORS.USER_PROFILE_INFO,
        params: {
          user_id: current_profile.attributes.user_id,
          profile_id: current_profile.id,
          profile_type: current_profile.type,
        },
      });
      const route_path_href = [origin_tenant, route_path.href].join('');
      _.defer(() => {
        window.location.href = route_path_href;
      });
    } else {
      const originalUrl = localStorage.getItem('impersonationOriginalUrl');
      if (originalUrl) {
        window.location.href = originalUrl;
      }
    }
    clearImpersonationState();
  };

  return {
    impersonate,
    isProfileActive,
    stopImpersonation
  };
}
